import type { Maybe } from '@passionware/monads';
import { booleanFilter } from 'v5/api/_common/query/filters/BooleanFilter';
import {
  EnumFilter,
  enumFilter,
  enumFilterSchema
} from 'v5/api/_common/query/filters/EnumFilter';
import {
  scheduledFilter,
  ScheduledFilter
} from 'v5/core/standard-date-filter/scheduled-filter';
import { coreFieldFiltersSchema } from 'v5/core/core-fields/core-fields-filters.schemas';
import { CoreFieldFilters } from 'v5/core/core-fields/core-fields-filters.types';
import { withCustomFieldFiltersCatchall } from 'v5/core/custom-fields/custom-fields-filters.schemas';
import { WithCustomFieldFilters } from 'v5/core/custom-fields/custom-fields-filters.types';
import { z } from 'zod';
import {
  Booking,
  Event,
  Expense,
  ResourceSlotAssignment
} from '../../../__types__';
import type { ExpenseLineUpdatePatch } from 'v5/api/resourcing/resourcing.mutation.types';
import type {
  BookingExpensesUpdate,
  BookingFeesUpdate,
  BookingRateAndFeesUpdate,
  BookingRateUpdate,
  BookingStatusUpdate,
  ExpenseLineCommittedUpdate
} from 'v5/core/production-resourcing/mutation-patches';
import type { ValidatedRow } from 'v5/platform/row-level-validation';
import {
  PaginatedResponse,
  paginationSchema,
  paginationUtils
} from '../_common/query/pagination';
import {
  searchUtils,
  withBuilderUtils,
  withFiltersUtils,
  WithOptionalFilters,
  WithPagination,
  withPaginationUtils,
  WithSearch,
  withSearchUtils,
  WithSorter,
  withSorterUtils
} from '../_common/query/queryUtils';
import { Overwrite } from 'v5/platform/typescript';
import {
  WithWorkspaceScoping,
  withWorkspaceScoping,
  workspaceScopingQueryUtils
} from '../_common/query/workspace-scoping';

export type {
  BookingExpensesUpdate,
  BookingFeesUpdate,
  BookingRateAndFeesUpdate,
  BookingRateUpdate,
  BookingStatusUpdate,
  ExpenseLineCommittedUpdate
} from 'v5/core/production-resourcing/mutation-patches';

export type BookingStatusType =
  | 'AVAILABLE'
  | 'UNAVAILABLE'
  | 'HOLD'
  | 'RELEASED'
  | 'EDITING'
  | 'CONFIRMED'
  | 'PENCILLED'
  | 'CANCELLED';

export const BOOKING_SORT_FIELDS = [
  'contact',
  'booking_type',
  'start_date',
  'end_date',
  'status',
  'createdAt',
  'updatedAt'
] as const;

export type BookingSortField = (typeof BOOKING_SORT_FIELDS)[number];

export type BookingTableColumnField = BookingSortField | 'duration' | 'owner';

export type BookingQuery = WithSearch &
  WithPagination &
  WithSorter<BookingSortField> &
  WithOptionalFilters<
    WithCustomFieldFilters<
      Overwrite<
        CoreFieldFilters,
        {
          status_type: EnumFilter<BookingStatusType>;
          status_id: EnumFilter<number>;
          booking_type_id: EnumFilter<number>;
          contact_id: EnumFilter<number>;
          booking_date_range: ScheduledFilter;
        }
      >
    >
  >;

/** Browsing-scope lens (Layer 2) for list search — from resolved app scope URL. */
export type BookingScopedQuery = WithWorkspaceScoping<BookingQuery>;

export type BookingListQuery = BookingQuery | BookingScopedQuery;

export function asGlobalBookingQuery<T extends BookingQuery>(
  searchQuery: T
): BookingScopedQuery {
  return withWorkspaceScoping(
    searchQuery,
    workspaceScopingQueryUtils.ofGlobal()
  );
}

/** Paginated list: each row is validated in {@link createBookingApi} (HTTP row parsing). */
export type BookingListResponse = PaginatedResponse<ValidatedRow<Booking>>;

export interface BulkArchiveRequest {
  ids: Booking['id'][];
}

/** File reference: relate existing file by id or provide full file details. */
export type InvoiceOrReceiptFilePayload =
  | { id: number }
  | {
      name: string;
      key: string;
      bucket: string;
      mime_type: string;
      size: number;
      provider: 'azure' | 's3';
    };

/** Structured document_data for invoice/receipt (amount in smallest currency unit). */
export interface DocumentDataPayload {
  reference?: string;
  amount?: number;
  currency?: string;
  status?: string;
}

/** Payload for creating a booking invoice (POST invoices). */
export interface CreateInvoicePayload {
  document_data?: DocumentDataPayload;
  file?: InvoiceOrReceiptFilePayload;
  status?: string;
  url?: string;
}

/** Payload for updating a booking invoice (PUT invoice). */
export interface UpdateInvoicePayload {
  document_data?: DocumentDataPayload;
  status?: string;
  file?: InvoiceOrReceiptFilePayload;
}

/** Payload for creating an expense receipt (POST receipts). */
export interface CreateReceiptPayload {
  document_data?: DocumentDataPayload;
  file?: InvoiceOrReceiptFilePayload;
  status?: string;
}

/** Payload for updating an expense receipt (PUT receipt). */
export interface UpdateReceiptPayload {
  document_data?: DocumentDataPayload;
  status?: string;
  file?: InvoiceOrReceiptFilePayload;
}

/**
 * Confirmation send/preview target. `enterpriseWorkspaceId` is the browsing-scope
 * template lens (Layer 2): absent means the organization (global) template.
 *
 * - `{ bookingId }` — singular v1 endpoint (supports `previewOnly`)
 * - `{ productionId }` — production bulk send only (no preview)
 */
export type BookingConfirmationRequest = {
  enterpriseWorkspaceId: Maybe<number>;
} & ({ bookingId: Booking['id'] } | { productionId: number });

/** Preview always uses the singular booking endpoint (`previewOnly`). */
export type BookingConfirmationPreviewRequest = Extract<
  BookingConfirmationRequest,
  { bookingId: number }
>;

/**
 * Which template the backend rendered. `workspaceId` is the workspace whose template it looked up
 * (the production's, else the one the user sends from; null = organization). `origin` is
 * `'global'` when that workspace has no template of its own and the organization one was used.
 */
export type BookingConfirmationTemplateScope = {
  origin: 'workspace' | 'global';
  workspaceId: number | null;
};

export type BookingConfirmationPreview = {
  content: string;
  /** Null when the backend does not report it. */
  template: BookingConfirmationTemplateScope | null;
};

/** The scope resolved by the backend has no confirmation template configured. */
export class MissingBookingConfirmationTemplateError extends Error {
  constructor() {
    super('No booking confirmation template configured for this scope');
    this.name = 'MissingBookingConfirmationTemplateError';
  }
}

export type BookingCreateEventDTO = Pick<
  Event,
  | 'date_type'
  | 'all_day'
  | 'start_date'
  | 'end_date'
  | 'start_timestamp'
  | 'end_timestamp'
  | 'recurrence'
  | 'estimated_daily_hours'
  | 'estimated_daily_minutes'
  | 'estimated_start_time'
  | 'estimated_end_time'
  | 'include_weekends'
  | 'note'
  | 'timezone'
>;

export type BookingCreateExpenseDTO = Pick<
  Expense,
  | 'id'
  | 'slot_category_id'
  | 'production_budget_expense_id'
  | 'booking_id'
  | 'name'
  | 'description'
  | 'commit_vendor'
  | 'commit_rate_currency'
  | 'commit_rate_amount'
  | 'commit_rate_interval'
  | 'commit_rate_quantity'
  | 'custom_fields'
>;

export type BookingCreateResourceSlotAssignmentDTO = Pick<
  ResourceSlotAssignment,
  'id' | 'resource_slot_id' | 'contact_id' | 'booking_id'
> & {
  events?: BookingCreateEventDTO[];
};

/**
 * Create payload used by caller/UI side.
 * Keeps shape explicit while still allowing custom and backend-specific fields.
 */
export interface BookingCreateDTO {
  contact_id: number;
  booking_type_id: number;
  status_id?: number;
  contact_rate_id?: number;
  rate_id?: number;
  rate_interval?: Booking['rate_interval'];
  rate_currency?: string;
  rate_amount?: number;
  rate_agency_fee?: number;
  rate_payroll_fee?: number;
  rate_processing_fee?: number;
  include_weekends?: boolean;
  estimated_daily_hours?: number;
  start_date?: Date | string | null;
  end_date?: Date | string | null;
  licence?: string;
  note?: string;
  submitted_date?: Date | string | null;
  agreed_date?: Date | string | null;
  pencilled_at?: Date | string | null;
  confirmed_at?: Date | string | null;
  cancelled_at?: Date | string | null;
  confirmation_sent_at?: Date | string | null;
  cancellation_sent_at?: Date | string | null;
  tasklist_id?: number;
  archived?: boolean;
  events?: BookingCreateEventDTO[];
  expenses?: BookingCreateExpenseDTO[];
  resource_slot_assignments?: BookingCreateResourceSlotAssignmentDTO[];
  custom_fields?: Booking['custom_fields'];
}

/**
 * Legacy general booking update (v1 shape). Prefer {@link BookingApi.updateResourcingBooking}
 * for production resourcing rate / fees / status patches.
 */
export type BookingUpdatePayload = Partial<Booking>;

/** Domain patches accepted by {@link BookingApi.updateResourcingBooking}. */
export type BookingResourcingUpdate =
  | BookingRateUpdate
  | BookingFeesUpdate
  | BookingRateAndFeesUpdate
  | BookingStatusUpdate;

export interface BookingApi {
  getList: (
    query: BookingListQuery,
    signal?: AbortSignal
  ) => Promise<BookingListResponse>;
  createItem: (data: BookingCreateDTO) => Promise<Booking>;
  getItem: (id: Booking['id']) => Promise<Booking>;
  getItems: (ids: Booking['id'][]) => Promise<Booking[]>;
  updateItem: (
    id: Booking['id'],
    data: BookingUpdatePayload
  ) => Promise<Booking>;
  /** Production resourcing: domain patch; v1 wire conversion in HTTP impl only. */
  updateResourcingBooking(
    id: Booking['id'],
    patch: BookingResourcingUpdate
  ): Promise<Booking>;
  updateResourcingBookingExpenses(
    id: Booking['id'],
    patch: BookingExpensesUpdate
  ): Promise<Booking>;
  getBookingExpenses(
    bookingIds: number[]
  ): Promise<Array<{ booking_id: number; expenses: Expense[] }>>;
  updateExpense(
    expenseId: number,
    data: ExpenseLineUpdatePatch
  ): Promise<unknown>;
  updateResourcingExpenseLine(
    expenseId: number,
    patch: ExpenseLineCommittedUpdate
  ): Promise<unknown>;
  /** Rendered confirmation email body and the template used — sends nothing and stamps nothing. */
  previewBookingConfirmation: (
    request: BookingConfirmationPreviewRequest
  ) => Promise<BookingConfirmationPreview>;
  /**
   * Sends confirmation email(s). One HTTP call: booking id → singular endpoint,
   * production id → bulk production endpoint.
   * Rejects with `MissingBookingConfirmationTemplateError` when the scope has no template.
   */
  sendBookingConfirmations: (
    request: BookingConfirmationRequest
  ) => Promise<void>;
  bulkArchive: (request: BulkArchiveRequest) => Promise<void>;
  bulkUnarchive: (request: BulkArchiveRequest) => Promise<void>;
  createInvoice: (
    bookingId: number,
    payload: CreateInvoicePayload
  ) => Promise<unknown>;
  updateInvoice: (
    bookingId: number,
    invoiceId: number,
    payload: UpdateInvoicePayload
  ) => Promise<unknown>;
  deleteInvoice: (bookingId: number, invoiceId: number) => Promise<void>;
  createReceipt: (
    expenseId: number,
    payload: CreateReceiptPayload
  ) => Promise<unknown>;
  updateReceipt: (
    expenseId: number,
    receiptId: number,
    payload: UpdateReceiptPayload
  ) => Promise<unknown>;
  deleteReceipt: (expenseId: number, receiptId: number) => Promise<void>;
}

export const bookingQueryUtils = withBuilderUtils({
  ...withSearchUtils<BookingQuery>(),
  ...withFiltersUtils<BookingQuery>(),
  ...withPaginationUtils<BookingQuery>(),
  ...withSorterUtils<BookingQuery>(),
  ofEmptySearchQuery: (): BookingQuery => ({
    search: '',
    sort: null,
    page: paginationUtils.ofDefault(),
    filters: {}
  }),
  ofDefault: (): BookingQuery => ({
    search: '',
    sort: { field: 'createdAt', order: 'desc' },
    page: paginationUtils.ofDefault(),
    filters: {
      archived: { operator: 'equal', value: false }
    }
  }),
  matchesQuery: (query: BookingQuery, booking: Booking) => {
    if (
      query.search &&
      !searchUtils.matches(query.search, booking.contact?.full_name || '')
    ) {
      return false;
    }

    // Status filter
    if (
      !enumFilter.matches(
        query.filters.status,
        (booking as any).status?.status_type
      )
    ) {
      return false;
    }

    // Status ID filter
    if (!enumFilter.matches(query.filters.status_id, booking.status_id ?? -1)) {
      return false;
    }

    // Booking type filter
    if (
      !enumFilter.matches(
        query.filters.booking_type_id,
        booking.booking_type_id
      )
    ) {
      return false;
    }

    // Contact filter
    if (
      !enumFilter.matches(query.filters.contact_id, booking.contact_id ?? -1)
    ) {
      return false;
    }

    // Booking date range filter - handled server-side by createDateRangeFilterConverter
    // Client-side filtering for date ranges is complex and should be done server-side
    // The createDateRangeFilterConverter properly converts to start_date and end_date constraints

    if (!booleanFilter.matches(query.filters.archived, booking.archived)) {
      return false;
    }

    return true;
  }
}).setInitialQueryFactory(x => x.ofEmptySearchQuery);

const strToNull = (str: unknown) => (str === '' ? null : str);

export const bookingSearchQuerySchema = z
  .object({
    search: z.preprocess(value => value || '', z.string()).default(''),
    filters: withCustomFieldFiltersCatchall(
      coreFieldFiltersSchema.extend({
        status_type: z.preprocess(
          strToNull,
          enumFilterSchema(
            z.enum([
              'AVAILABLE',
              'UNAVAILABLE',
              'HOLD',
              'RELEASED',
              'EDITING',
              'CONFIRMED',
              'PENCILLED',
              'CANCELLED'
            ])
          ).nullable()
        ),
        status_id: z.preprocess(
          strToNull,
          enumFilterSchema(z.coerce.number()).nullable()
        ),
        booking_type_id: z.preprocess(
          strToNull,
          enumFilterSchema(z.coerce.number()).nullable()
        ),
        contact_id: z.preprocess(
          strToNull,
          enumFilterSchema(z.coerce.number()).nullable()
        ),
        booking_date_range: z.preprocess(
          strToNull,
          scheduledFilter.schema.nullable()
        )
      })
    ).partial(),
    page: paginationSchema,
    sort: z
      .preprocess(
        strToNull,
        z
          .object({
            field: z.enum(BOOKING_SORT_FIELDS),
            order: z.enum(['asc', 'desc'])
          })
          .nullable()
      )
      .default(null)
  })
  .catch(e => {
    console.error('Error parsing booking search query', e.issues);
    return bookingQueryUtils.ofEmptySearchQuery();
  });

export const bookingQuerySchema = bookingSearchQuerySchema;
